<script>
  import Page from '@components/page'
  import Button from '@components/buttons/button.svelte'
  import Table from '@components/table'
  import Image from '@components/general/image.svelte'
  import Dropdown from '@components/general/dropdown.svelte'
  import Tag from '@components/general/tag.svelte'
  import SyncModal from '@components/videos/sync-modal.svelte'
  import TagForm from '@components/tags/form.svelte'
  import UpdateForm from '@components/videos/form.svelte'
  import { router } from '@inertiajs/svelte'
  import { useSelection } from '@hooks/use-selection.svelte'
  import { usePageParam } from '@hooks/use-page-param.svelte'
  import { User, RefreshCw, Eye, EyeOff, CheckCheck, CheckCircle, Lock, Unlock, Wand, ChevronLeft, Trash2, Pencil, ExternalLink } from 'lucide-svelte'
  import { formatDuration } from '@lib/utils'
  import ago from 's-ago'

  let { videos = [], pagination, ids, last_synced_at } = $props()

  let selection = $derived(useSelection({ initialItems: videos, allIds: ids }))
  let selectedTag = $state(null)
  let selectedVideo = $state(null)

  let title = "Manage Videos"
  let links = [{ text: "Admin", href: "/admin" }]

  let status = usePageParam({ key: "status", defaultValue: "active", items: [
    { value: "active", label: "Status: Active", icon: Eye },
    { value: "inactive", label: "Status: Hidden", icon: EyeOff },
    { value: "all", label: "Status: All", icon: CheckCheck }
  ] })

  let access = usePageParam({ key: "access", defaultValue: "all", items: [
    { value: "free", label: "Access: Free", icon: Unlock },
    { value: "premium", label: "Access: Premium", icon: Lock },
    { value: "all", label: "Access: Any", icon: CheckCheck },
  ] })

  let sync = $state({ open: false, processing: false, results: null, error: null })

  let syncButtonHint = $derived(last_synced_at ? `Last synced ${ago(new Date(last_synced_at))}` : "Never synced")

  function changeStatus(video, status) {
    selection.clear()
    router.patch(`/admin/videos/${video.id}/status`, { status })
  }

  function changeAccess(video, access) {
    selection.clear()
    router.patch(`/admin/videos/${video.id}/access`, { access })
  }

  function batchChangeStatus(status) {
    if (!window.confirm(`Are you sure you want to make all selected videos ${status}?`)) return
    router.post(`/admin/videos/batch_status`, { status, ids: selection.ids }, {
      onSuccess: selection.clear()
    })
  }

  function batchChangeAccess(access) {
    if (!window.confirm(`Are you sure you want to make all selected videos ${access}?`)) return
    router.post(`/admin/videos/batch_access`, { access, ids: selection.ids }, {
      onSuccess: selection.clear()
    })
  }

  function batchDelete() {
    if (!window.confirm(`Delete the selected videos from the platform? They will remain in your Vimeo library.`)) return
    // We use visit with method and data here as the router.delete method doesn't have a shorthand for data
    router.visit(`/admin/videos/batch_delete`, { method: "delete", data: { ids: selection.ids } }, { onSuccess: selection.clear() })
  }

  function resetSync() {
    sync = { open: false, processing: false, results: null, error: null }
  }

</script>

<Page.Layout title={title}>
  <Page.Navigation title="Videos" {links} />

  <Page.Titles {title} subtitle={pagination?.summary || ""}>
    {#snippet buttons()}
      <Button text="Public page" path="/videos" BeforeIcon={ChevronLeft} />
      <Button onclick={() => sync.open = true} text="Vimeo sync" Icon={RefreshCw} hint={syncButtonHint} />
    {/snippet}
  </Page.Titles>

  <Page.Content>

    <Table.FilterBar {pagination} {selection}>
      {#snippet filters()}

        <!-- Status filter -->
        <Dropdown buttonProps={{ text: status.label || "Status", size: "md", Icon: status.icon }} width="16rem" align="start" itemSize="sm">
          {#snippet contents(item)}
            {#each status.items as statusItem (statusItem.value)}
              {@render item({ text: statusItem.label, icon: statusItem.icon, onSelect: () => { selection.clear(); status.setValue(statusItem.value) } })}
            {/each}
          {/snippet}
        </Dropdown>

        <!-- Access filter -->
        <Dropdown buttonProps={{ text: access.label || "Access", size: "md", Icon: access.icon }} width="16rem" align="start" itemSize="sm">
          {#snippet contents(item)}
            {#each access.items as accessItem (accessItem.value)}
              {@render item({ text: accessItem.label, icon: accessItem.icon, onSelect: () => { selection.clear(); access.setValue(accessItem.value) } })}
            {/each}
          {/snippet}
        </Dropdown>

        <!-- Actions -->
        {#if selection.populated}
          <Dropdown buttonProps={{ text: `${selection.count} selected`, size: "md", Icon: Wand }} width="16rem" align="start" itemSize="sm">
            {#snippet contents(item)}
              {@render item({ text: "Mark active", icon: Eye, onSelect: () => batchChangeStatus("active") })}
              {@render item({ text: "Mark hidden", icon: EyeOff, onSelect: () => batchChangeStatus("inactive") })}
              {@render item({ text: "Make available to all", icon: Unlock, onSelect: () => batchChangeAccess("free") })}
              {@render item({ text: "Make premium only", icon: Lock, onSelect: () => batchChangeAccess("premium") })}
              {@render item({ text: "Delete from platform", icon: Trash2, onSelect: () => batchDelete() })}
            {/snippet}
          </Dropdown>
        {/if}

      {/snippet}
    </Table.FilterBar>

    <Table.Root empty={!videos.length} emptyMessage="No videos found">

      <Table.Head>
        <!-- 6% -->
        <Table.SelectionDropdownCell {selection} /> 
        <Table.Header id="image" width="13%" align="center">Image</Table.Header>
        <Table.Header id="details" width="45%">Details</Table.Header>
        <Table.Header id="status" width="12%" align="center">Status</Table.Header>
        <Table.Header id="access" width="12%" align="center">Access</Table.Header>
        <Table.Header id="actions" width="10%" align="center">Actions</Table.Header>
      </Table.Head>

      <Table.Body>
        {#each videos as video (video.id)}

          <Table.Row selected={selection.has(video.id)}>

            <!-- Checkbox -->
            <Table.Cell width="6%" align="center">
              <input type="checkbox" checked={selection.has(video.id)} onclick={e => selection.toggle(video.id, e)} />
            </Table.Cell>

            <!-- Video image -->
            <Table.Cell width="13%" align="center">
              <Image src={video.vimeo_image_url} alt={video.name} loading="lazy" class="h-12 w-20 mx-auto border border-gray-200/70 bg-gray-100" />
            </Table.Cell>

            <!-- Video details -->
            <Table.Cell width="45%">

              <div class="flex flex-col gap-1">

                <!-- Name and duration -->
                <div class="flex items-center gap-1 group/name">
                  <Button variant="link" class="text-gray-800 font-medium leading-none" onclick={() => selectedVideo = video}>{video.name}</Button>
                  <Button variant="naked" Icon={ExternalLink} hint="View" path={`/videos/${video.id}`} class="text-gray-400 hover:text-gray-700 opacity-0 group-hover/name:opacity-100 transition-opacity" iconProps={{ size: 12 }} />
                  <span class="text-xs text-gray-500 leading-none">- {formatDuration(video.duration)}</span>
                </div>

                <!-- Description -->
                <div class="text-smaller text-gray-500 truncate max-w-152">{video.description || 'No description'}</div>

                <!-- Tags -->
                <div class="flex items-center gap-1 flex-wrap">
                  {#each video.tags as tag (tag.id)}
                    <Tag {tag} size="xs" onSelect={tag => selectedTag = tag} />
                  {/each}

                  <!-- Presenter -->
                  {#if video.contributor}
                    <User size={10} />
                    <span class="text-xs text-gray-500">{video.contributor.name}</span>
                  {/if}
                </div>

              </div>
            </Table.Cell>

            <!-- Video status -->
            <Table.Cell width="12%" align="center">
              {#if video.active}
                <Button variant="primary" size="xs" text="Active" Icon={CheckCircle} hint="Click to make inactive" onclick={() => changeStatus(video, "inactive")} />
              {:else}
                <Button variant="secondary" size="xs" text="Hidden" Icon={EyeOff} iconClass="text-gray-400" hint="Click to make active" onclick={() => changeStatus(video, "active")} />
              {/if}
            </Table.Cell>

            <!-- Video access -->
            <Table.Cell width="12%" align="center">
              <Dropdown
                width="12rem"
                text={video.free ? "Free" : "Premium"}
                buttonProps={{
                  text: video.free ? "Free" : "Premium",
                  variant: video.free ? "confirm" : "primary",
                  size: "xs",
                  disabled: !video.active,
                  hint: !video.active ? "Video currently hidden" : null
                }}>
                {#snippet contents(item)}
                  {@render item({ text: "Free", icon: Unlock, onSelect: () => changeAccess(video, "free") })}
                  {@render item({ text: "Premium", icon: Lock, onSelect: () => changeAccess(video, "premium") })}
                {/snippet}
              </Dropdown>
            </Table.Cell>

            <!-- Actions -->
            <Table.Cell width="10%" align="center">
              <Button variant="naked" Icon={Pencil} hint="Edit" onclick={() => selectedVideo = video} class="justify-center" />
            </Table.Cell>

          </Table.Row>

        {/each}
      </Table.Body>

    </Table.Root>

    <Table.Pagination {pagination} />

  </Page.Content>

</Page.Layout>

<SyncModal {sync} lastSyncText={syncButtonHint} reset={resetSync} />

<TagForm tag={selectedTag} open={selectedTag} onClose={() => selectedTag = null} onSuccess={() => selectedTag = null} />

<UpdateForm video={selectedVideo} open={selectedVideo} onClose={() => selectedVideo = null} onSuccess={() => selectedVideo = null} />
